import { isEmpty, isNil, uniqBy } from 'ramda';

import { EActivityEventType, ETripEvent, IDaysRange, ITripEvent, TTripEvent } from 'detailsv2/structure';
import { tripEventSelector } from 'store/itinerariesItem/chronologicalDetails/selectors';

import { DEFAULT_PERIOD } from '../constansts/drag-and-drop';
import { CalendarEvent, ICalendarPeriod, TCalendarDay } from '../types';
import DaysGenerator from './days-generator';

const selectTripEventById = tripEventSelector as (id: ITripEvent<ETripEvent>['id']) => (state: any) => TTripEvent;

const checkIsEndDaysExistsAlways = (event: CalendarEvent) => {
  return [ETripEvent.Flight, ETripEvent.Transfer, ETripEvent.Accommodation].includes(event.type);
};

const getNumberOfDays = (event: CalendarEvent) => {
  if (!event.daysRange.end) return 0;

  return event.daysRange.end - event.daysRange.start;
};

export const getCurrentGrabbedDay = (daysRange: IDaysRange, draggingStartDay: number | null) => {
  if (daysRange.end && draggingStartDay) {
    return draggingStartDay - daysRange.start + 1;
  }

  return 1;
};

const getUpdatedDaysRange = (event: CalendarEvent, dayNumber: number, draggingStartDay: number | null) => {
  const numberOfDays = getNumberOfDays(event);
  if (numberOfDays === 0 && !checkIsEndDaysExistsAlways(event)) {
    return { start: dayNumber, end: dayNumber };
  }

  if (numberOfDays >= 1) {
    const eventLength = numberOfDays + 1;
    const currentGrabbedDay = getCurrentGrabbedDay(event.daysRange, draggingStartDay);

    if (draggingStartDay === event.daysRange.end) {
      const start = dayNumber - numberOfDays;
      const end = dayNumber >= eventLength ? dayNumber : eventLength;

      return { start: start > 0 ? start : 1, end };
    }

    if (draggingStartDay !== event.daysRange.start) {
      const start = dayNumber + 1 - currentGrabbedDay;
      const lastDay = dayNumber + (eventLength - currentGrabbedDay);
      const end = lastDay >= eventLength ? lastDay : eventLength;

      return { start: start > 0 ? start : 1, end };
    }
  }

  return { start: dayNumber, end: dayNumber + numberOfDays };
};

const getUpdatedDaysRangeForExtending = (event: CalendarEvent, day: Pick<TCalendarDay, 'dayNumber'>) => {
  const prevStartDayNumber = event.daysRange.start;
  const isSameDayOrBefore = prevStartDayNumber >= day.dayNumber;

  if (isSameDayOrBefore) {
    if (event.data && 'type' in event.data && event.data?.type === EActivityEventType.MultiDay) {
      return { start: prevStartDayNumber, end: prevStartDayNumber + 1 };
    }

    if (!checkIsEndDaysExistsAlways(event)) return { start: prevStartDayNumber, end: null };

    return { start: prevStartDayNumber, end: prevStartDayNumber };
  }

  return { start: prevStartDayNumber, end: day.dayNumber };
};

const checkIsEventInDay = (event: CalendarEvent, dayNumber: number): boolean => {
  return event.daysRange.end
    ? event.daysRange.start <= dayNumber && event.daysRange.end >= dayNumber
    : event.daysRange.start === dayNumber;
};

const getAllEventsFromDay = (events: CalendarEvent[], dayNumber: number): CalendarEvent[] => {
  return events.filter((e) => checkIsEventInDay(e, dayNumber));
};

const getUpdatedPeriodFromEventList = (events: CalendarEvent[]) => {
  if (isEmpty(events)) {
    return DEFAULT_PERIOD;
  } else {
    let start: number | null = null;
    let end: number | null = null;

    events.forEach((e) => {
      const eventEnd = e.daysRange.end || e.daysRange.start;

      if (!start || (start && e.daysRange.start < start)) {
        start = e.daysRange.start;
      }

      if ((!end && eventEnd) || (eventEnd && end && eventEnd > end)) {
        end = eventEnd;
        return;
      }
    });

    return { start, end };
  }
};

const getDayNumberFromCursor = (cursorX: number, cursorY: number): number | null => {
  const elements = document.querySelectorAll('[data-calendar-day-number]');

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const rect = element.getBoundingClientRect();

    if (rect.left <= cursorX && cursorX <= rect.right && rect.top <= cursorY && cursorY <= rect.bottom) {
      if (element.getAttribute('data-calendar-day-number')) {
        return Number(element.getAttribute('data-calendar-day-number'));
      } else {
        const childElements = element.querySelectorAll('[data-calendar-day-number]');

        for (let j = 0; j < childElements.length; j++) {
          const childElement = childElements[j];
          const childRect = childElement.getBoundingClientRect();

          if (
            childRect.left <= cursorX &&
            cursorX <= childRect.right &&
            childRect.top <= cursorY &&
            cursorY <= childRect.bottom
          ) {
            return Number(childElement.getAttribute('data-calendar-day-number'));
          }
        }
      }
    }
  }

  return null;
};

const getNewSelectedPeriod = (previousSelectedPeriod: ICalendarPeriod, dayNumber: number) => {
  if (isNil(previousSelectedPeriod.start) && isNil(previousSelectedPeriod.end)) {
    return { start: dayNumber, end: dayNumber };
  }

  if (previousSelectedPeriod.start && previousSelectedPeriod.end) {
    if (dayNumber === previousSelectedPeriod.start || dayNumber === previousSelectedPeriod.end) {
      return { start: dayNumber, end: dayNumber };
    }

    if (
      (dayNumber < previousSelectedPeriod.end && dayNumber > previousSelectedPeriod.start) ||
      dayNumber > previousSelectedPeriod.end
    ) {
      return { start: previousSelectedPeriod.start, end: dayNumber };
    }

    if (dayNumber < previousSelectedPeriod.start) {
      return { start: dayNumber, end: previousSelectedPeriod.end };
    }
  }

  return previousSelectedPeriod;
};

const checkIsDayInPeriod = (dayNumber: number, period: ICalendarPeriod) => {
  return Boolean(period.start && period.end && dayNumber >= period.start && dayNumber <= period.end);
};

const checkIsNeedToMergeEvents = (
  previousPeriod: ICalendarPeriod,
  newPeriod: ICalendarPeriod,
  events: CalendarEvent[],
) => {
  if (!isEmpty(events)) {
    if (
      (!previousPeriod.start && !previousPeriod.end && newPeriod.start === newPeriod.end) ||
      (previousPeriod.start && previousPeriod.end && newPeriod.start !== newPeriod.end)
    ) {
      return true;
    }
  }

  return false;
};

const getEventsFromPeriod = (
  events: CalendarEvent[],
  period: ICalendarPeriod,
  selectedEvents: CalendarEvent[],
  previousPeriod: ICalendarPeriod,
) => {
  const periodEvents: CalendarEvent[] = [];

  if (period.start && period.end) {
    for (let i = period.start; i <= period.end; i++) {
      const dayEvents = getAllEventsFromDay(events, i);
      periodEvents.push(...dayEvents);
    }
  }

  const uniquePeriodEvents = Array.from(new Set(periodEvents));

  const allSelectedEvents = checkIsNeedToMergeEvents(previousPeriod, period, selectedEvents)
    ? [...selectedEvents, ...uniquePeriodEvents]
    : uniquePeriodEvents;

  if (period.end && previousPeriod.end && period.end < previousPeriod.end) {
    return allSelectedEvents.filter((event) => period.end && event.daysRange.start <= period.end);
  }

  return uniqBy((e) => e.id, allSelectedEvents);
};

export {
  DaysGenerator,
  checkIsEndDaysExistsAlways,
  getUpdatedDaysRange,
  selectTripEventById,
  getUpdatedDaysRangeForExtending,
  getUpdatedPeriodFromEventList,
  getDayNumberFromCursor,
  getNewSelectedPeriod,
  checkIsDayInPeriod,
  getEventsFromPeriod,
};
